import React from 'react';
import { Box, Typography, LinearProgress, Chip } from '@mui/material';
import { TrendingUp, EmojiEvents, Star } from '@mui/icons-material';
import { motion } from 'framer-motion';

interface ProgressBarProps {
  currentLevel: number;
  maxLevel: number;
  progress: number; // 0-100
  levelName: string;
  nextLevelName?: string;
  animated?: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  currentLevel,
  maxLevel,
  progress,
  levelName,
  nextLevelName,
  animated = true
}) => {
  const isMaxLevel = currentLevel >= maxLevel;

  return (
    <motion.div
      initial={animated ? { opacity: 0, y: 20 } : undefined}
      animate={animated ? { opacity: 1, y: 0 } : undefined}
      transition={animated ? { duration: 0.5 } : undefined}
    >
      <Box sx={{ width: '100%', p: 3, backgroundColor: 'white', borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmojiEvents sx={{ color: 'secondary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Level {currentLevel}
            </Typography>
            <Chip
              label={levelName}
              color="primary"
              size="small"
              icon={<Star />}
            />
          </Box>
          <Typography variant="body2" color="textSecondary">
            {progress.toFixed(0)}% Complete
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 6,
                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #f57c00 100%)',
              },
            }}
          />
        </Box>

        {!isMaxLevel && nextLevelName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp sx={{ color: 'success.main', fontSize: 16 }} />
            <Typography variant="body2" color="textSecondary">
              Next: <strong>{nextLevelName}</strong>
            </Typography>
          </Box>
        )}

        {isMaxLevel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmojiEvents sx={{ color: 'secondary.main', fontSize: 16 }} />
            <Typography variant="body2" color="secondary.main" sx={{ fontWeight: 'bold' }}>
              Maximum level achieved! 🎉
            </Typography>
          </Box>
        )}
      </Box>
    </motion.div>
  );
};

export default ProgressBar;